import type {
  CheckAccessResponse,
  ProcessoPermissaoDto,
  UsuarioBuscaDto,
} from '@/types/processo/processoPermissaoTypes';
import { createSlice } from '@reduxjs/toolkit';
import {
  atualizarPermissao,
  buscarUsuarios,
  criarPermissao,
  listarPermissoes,
  removerPermissao,
  verificarAcesso,
} from './actions';

interface ProcessoPermissoesState {
  permissoes: ProcessoPermissaoDto[];
  usuariosBusca: UsuarioBuscaDto[];
  acessoAtual: CheckAccessResponse | null;
  loading: boolean;
  error: string | null;
}

const initialState: ProcessoPermissoesState = {
  permissoes: [],
  usuariosBusca: [],
  acessoAtual: null,
  loading: false,
  error: null,
};

const processoPermissoesSlice = createSlice({
  name: 'processoPermissoes',
  initialState,
  reducers: {
    limparBusca: (state) => {
      state.usuariosBusca = [];
    },
    limparPermissoes: (state) => {
      state.permissoes = [];
    },
    limparErro: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(buscarUsuarios.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(buscarUsuarios.fulfilled, (state, action) => {
        state.loading = false;
        state.usuariosBusca = action.payload;
      })
      .addCase(buscarUsuarios.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      .addCase(listarPermissoes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(listarPermissoes.fulfilled, (state, action) => {
        state.loading = false;
        state.permissoes = action.payload;
      })
      .addCase(listarPermissoes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      .addCase(criarPermissao.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(criarPermissao.fulfilled, (state, action) => {
        state.loading = false;
        state.permissoes.push(action.payload);
      })
      .addCase(criarPermissao.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      .addCase(atualizarPermissao.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(atualizarPermissao.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.permissoes.findIndex((p) => p.id === action.payload.id);
        if (index !== -1) {
          state.permissoes[index] = action.payload;
        }
      })
      .addCase(atualizarPermissao.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      .addCase(removerPermissao.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(removerPermissao.fulfilled, (state, action) => {
        state.loading = false;
        state.permissoes = state.permissoes.filter(
          (p) =>
            !(
              p.processoId === action.payload.processoId && p.usuarioId === action.payload.usuarioId
            ),
        );
      })
      .addCase(removerPermissao.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      .addCase(verificarAcesso.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verificarAcesso.fulfilled, (state, action) => {
        state.loading = false;
        state.acessoAtual = action.payload;
      })
      .addCase(verificarAcesso.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { limparBusca } = processoPermissoesSlice.actions;
export default processoPermissoesSlice.reducer;
